import React, { useEffect, useState } from 'react';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import { CssBaseline, Box } from '@mui/material';
import createCache from '@emotion/cache';
import { CacheProvider } from '@emotion/react';
import stylisRTLPlugin from 'stylis-plugin-rtl';
import { prefixer } from 'stylis';
import { crearTema } from './theme/theme';
import { ProveedorModo, useModo } from './config/modo';
import { ProveedorLector, useLector } from './config/lector';
import Header from './components/Layout/Header';
import Footer from './components/Layout/Footer';
import { BrowserRouter } from "react-router-dom";
import Router from "./Router";
import { useTranslation } from 'react-i18next';
import { RTL_LANGUAGES } from './i18n';
import { getSiteConfig, varianteDelSitio } from './config/sites';
import { AuthProvider } from './auth/AuthProvider';
import { SiteProvider } from './site/SiteProvider';
import { prefijoDeLaRuta, idiomaDeLaRuta, publicarHreflang, destinoPorNavegador,
         idiomaDelLector, direccionEnIdioma, idiomaServible } from './config/idioma_url';
//import CountdownPage from './pages/CountdownPage';

const rtlCache = createCache({ key: 'muirtl', stylisPlugins: [prefixer, stylisRTLPlugin] });
const ltrCache = createCache({ key: 'muiltr' });

function Aplicacion() {
  const { i18n } = useTranslation();
  const { modo } = useModo();
  const { lector } = useLector();
  const [isRTL, setIsRTL] = useState(RTL_LANGUAGES.includes(i18n.language));

  /* 🔴 EL IDIOMA VIENE DE LA DIRECCION, y manda sobre lo guardado (28-ago).
     Si alguien abre `/de/canalizaciones`, tiene que ver alemán aunque la última
     vez eligiera español: el enlace que le mandaron dice en qué idioma quiere
     leerlo. Sin esto, un enlace compartido llegaba en el idioma del receptor.
     Detalle y forma de las direcciones → config/idioma_url.js */
  const idiomaUrl = idiomaDeLaRuta();

  /* Una direccion sirve SIEMPRE el mismo idioma: con prefijo, el suyo; sin
     prefijo, el del dominio. Nunca «el del navegador de quien mire», porque
     entonces Google indexaria una version al azar y el hreflang mentiria. */
  /* Y por encima de todo, el de su cuenta si la tiene: el idioma le acompaña
     entre desde donde entre. → config/idioma_url.js */
  const suyo = idiomaDelLector(lector);

  useEffect(() => {
    /* ⚠️ SIEMPRE PASADO POR `idiomaServible`. Fijar aqui un idioma que el sitio
       ya no sirve deja la pagina SIN TEXTOS —con las claves crudas a la vista—,
       y pasa solo: basta que el visitante tuviera otro guardado de antes, o que
       el idioma por defecto del dominio no este entre los traducidos. */
    const debido = idiomaServible(suyo || idiomaUrl);
    if (i18n.language !== debido) i18n.changeLanguage(debido);
  }, [suyo, idiomaUrl, i18n]);

  /* Si tiene cuenta y esta en una direccion de otro idioma —le compartieron un
     enlace, por ejemplo— se le lleva a la suya, que es la que sabe leer. */
  useEffect(() => {
    if (suyo && idiomaUrl !== suyo) {
      const destino = direccionEnIdioma(suyo);
      if (destino !== window.location.pathname + window.location.search) {
        window.location.replace(destino);
      }
    }
  }, [suyo, idiomaUrl]);

  /* Y la deteccion por navegador no cambia el contenido: LLEVA a su direccion.
     Quien llega con el navegador en aleman acaba en `/de/...`, que es la que
     puede compartir y la que Google puede indexar como alemana. Una sola vez. */
  useEffect(() => {
    if (idiomaDelLector(lector)) return;   // con cuenta, manda la cuenta
    const destino = destinoPorNavegador();
    if (destino) window.location.replace(destino);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* Las etiquetas hreflang: se rehacen en cada cambio de idioma porque apuntan
     a esta misma pagina en las demas lenguas. */
  useEffect(() => { publicarHreflang(); }, [idiomaUrl, i18n.language]);

  useEffect(() => {
    const handleLangChange = (lng) => {
      const rtl = RTL_LANGUAGES.includes(lng);
      setIsRTL(rtl);
      document.documentElement.dir = rtl ? 'rtl' : 'ltr';
      /* Def 75: en espanol se declara la VARIANTE del sitio (es-ES o es-419),
         no un «es» a secas. Es lo que leen el navegador, los buscadores y los
         lectores de pantalla, y es la diferencia entre un texto escrito para
         quien lo lee y uno traido de otro pais. Los demas idiomas no se tocan. */
      document.documentElement.lang = lng === 'es' ? varianteDelSitio() : lng;
    };
    i18n.on('languageChanged', handleLangChange);
    handleLangChange(i18n.language);
    return () => i18n.off('languageChanged', handleLangChange);
  }, [i18n]);

  const activeTheme = createTheme(crearTema(modo), { direction: isRTL ? 'rtl' : 'ltr' });

  const childProps = {
    isAuthenticated: true,
    userRole: "admin"
  };
  /*return (
    <CountdownPage />
  );*/
  return (
    <CacheProvider value={isRTL ? rtlCache : ltrCache}>
    <ThemeProvider theme={activeTheme}>
      <CssBaseline />
      {/* El prefijo del idioma. Con `basename` los 27 enlaces que ya existen
          —`to="/canalizaciones"`— se resuelven solos como `/de/canalizaciones`:
          no hay que reescribir ninguno, y por tanto no hay forma de olvidarse
          de uno. El idioma por defecto va sin prefijo, asi que ninguna
          direccion que ya exista se rompe. */}
      <BrowserRouter basename={prefijoDeLaRuta()}>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          minHeight: '100vh',
          bgcolor: 'background.default',
        }}
      >
        <Header />
        <Box
          component="main"
          sx={{
            flexGrow: 1,
            py: 0,
            mt: 8,
            color: 'text.primary',
          }}
        >
            <Router childProps={childProps} />
        </Box>
        <Footer />
      </Box>
      </BrowserRouter>
    </ThemeProvider>
    </CacheProvider>
  );
}

// El proveedor de modo envuelve a la aplicacion para que el tema pueda leerlo.
function App() {
  return (
    <SiteProvider>
    <ProveedorModo>
      <AuthProvider>
      <ProveedorLector>
        <Aplicacion />
      </ProveedorLector>
      </AuthProvider>
    </ProveedorModo>
    </SiteProvider>
  );
}

export default App;